import { put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnoOrganisationsChildrenResponse } from 'APIPackage/CatalogsModule/interfaces/IKnoOrganisationsChildrenResponse';
import { fetchKnoErvkOrganizationsSearchByRelevance } from 'APIPackage/CatalogsModule/services/knoOrganisationsApi';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import {
  changeKnoGetMeasureHistoryChanges,
  changeKnoSetMeasureHistoryChanges,
  setErvkOrganisations,
  setMeasureNumbers,
  startProcessFormSubmit,
} from '../actions/ChangeKnoInMeasureActions';
import { ChangeKnoInMeasureActionsEnum } from '../enums/ChangeKnoInMeasureActionsEnum';
import {
  getErvkOrganisationsSearchQuery,
  getMeasureNumbersSearchQuery,
} from '../selectors/changeKnoInMeasureSelectors';
import { addNoticeAction, removeAllNoticesAction } from '../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import {
  changeKnoForMeasure,
  fetchMeasureHistoryChanges,
  searchMeasuresByNumber,
} from '../../APIPackage/KnmModule/services/knmMeasureKnoApi';
import { IMeasureKnoChangeHistoryItem } from '../../APIPackage/KnmModule/interfaces/IMeasureKnoChangeHistoryItem';
import { IResponseError } from '../../APIPackage/MainModule/interfaces/IResponseError';
import { IMeasureNumberSearchItem } from '../../APIPackage/KnmModule/interfaces/IMeasureNumberSearchItem';
import { KnmVersionsEnum } from '../../APIPackage/KnmModule/enums/KnmVersionsEnum';

const PAGE_SIZE = '10';

export function* watchChangeKnoInMeasureSaga() {
  yield takeEvery(ChangeKnoInMeasureActionsEnum.CHANGE_KNO_ERVK_ORGANIZATION_GET, getOrganisationsSaga);
  yield takeEvery(ChangeKnoInMeasureActionsEnum.CHANGE_KNO_MEASURE_NUMBERS_GET, getMeasureNumbersSaga);
  yield takeEvery(ChangeKnoInMeasureActionsEnum.CHANGE_KNO_FORM_SUBMIT, changeKnoFormSubmitSaga);
  yield takeEvery(ChangeKnoInMeasureActionsEnum.CHANGE_KNO_MEASURE_HISTORY_CHANGES_GET, getMeasureHistoryChangesSaga);
}

function* getMeasureHistoryChangesSaga() {
  function* funcTry() {
    const res: IMeasureKnoChangeHistoryItem[] = yield fetchMeasureHistoryChanges();

    yield put(changeKnoSetMeasureHistoryChanges(res));
  }

  yield sagaWrapper({ funcTry });
}

function* getOrganisationsSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const searchQuery = getErvkOrganisationsSearchQuery(state);

    if (searchQuery) {
      const isNumber = /^\d+$/.test(searchQuery);

      const res: IKnoOrganisationsChildrenResponse = yield fetchKnoErvkOrganizationsSearchByRelevance({
        query: searchQuery,
        adminLevel: undefined,
        searchCriteria: isNumber ? 'OGRN' : 'NAME',
        order: 'fullName',
        page: '0',
        size: PAGE_SIZE,
      });

      yield put(setErvkOrganisations(res.list));
    }
  }

  yield sagaWrapper({ funcTry });
}

function* getMeasureNumbersSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const searchQuery = getMeasureNumbersSearchQuery(state);

    if (searchQuery) {
      const res: IMeasureNumberSearchItem[] = yield searchMeasuresByNumber(searchQuery);

      yield put(setMeasureNumbers(res));
    }
  }

  yield sagaWrapper({ funcTry });
}

function* changeKnoFormSubmitSaga(action: ActionType<typeof startProcessFormSubmit>) {
  function* funcTry() {
    yield put(removeAllNoticesAction());

    const { erpId, changeReason, system } = action.payload;
    const selectedErvkKnoOrganization = action.payload.ervkKnoOrganization;
    const ervkVersionUuid = selectedErvkKnoOrganization?.ervkVersionUuid;
    const ervkUuid = selectedErvkKnoOrganization?.guid?.toString();

    if (!erpId || !ervkVersionUuid || !system) {
      return;
    }

    try {
      const isErp = system === KnmVersionsEnum.ERP;

      yield changeKnoForMeasure(erpId, ervkVersionUuid, ervkUuid, changeReason, isErp);
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.CHANGE_KNO,
          renderContent: 'Операция выполнена успешно',
        }),
      );
    } catch (err) {
      const error: IResponseError = err as IResponseError;

      if (error.clientErrorCode === 'E22001') {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.CHANGE_KNO,
            renderContent: 'У мероприятия не указан вид контроля',
          }),
        );
      } else if (error.clientErrorCode === 'E22002') {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.CHANGE_KNO,
            renderContent: 'Для указанного КНО не применим вид контроля, указанный в мероприятии',
          }),
        );
      } else if (error.clientErrorCode === 'E22003') {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.CHANGE_KNO,
            renderContent: 'Изменение КНО в мероприятии не требуется',
          }),
        );
      } else {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.CHANGE_KNO,
            renderContent: 'Ошибка при выполнении операции',
          }),
        );
      }
    }

    yield put(changeKnoGetMeasureHistoryChanges());
  }

  yield sagaWrapper({ funcTry });
}
